import { ProjectPlanResponse } from "./project-plan-builder";

/**
 * Занятость сотрудника на других проектах — фон строки на «Плановом графике работ».
 *
 * Строка сотрудника внутри проекта показывает только его работу на этом проекте. Чтобы
 * решить, куда сдвинуть работу (и откуда взялась перегрузка), нужна вся его загрузка:
 * отпуска уже рисуются полосами, а работа на других проектах (и в других разделах этого
 * же проекта) — приглушённой «занятостью» под своими полосами. Это не блок проекта: его
 * не тянут, по нему не считаются сроки, он только показывает, что сотрудник занят.
 *
 * Считается по ответу графика с наложенным черновиком — в предпросмотре правок занятость
 * уже на новых местах.
 */

/** Отрезок занятости в колонках сетки: один проект и раздел, подряд идущие рабочие дни */
export interface BusyBand {
    orderId: string;
    /** Раздел работы; пусто — без раздела */
    contentGuid: string;
    start: number;
    length: number;
    from: string;
    to: string;
    /** Номер проекта «0494» — подпись полосы */
    inventory: string;
    /** «0494 ЖД Лизюкова 2» — подсказка */
    title: string;
    /** Шифр раздела «КР3», если работа на разделе */
    sectionCode: string;
}

export function busyBands(response: ProjectPlanResponse | null, days: string[]): Map<string, BusyBand[]> {
    const result = new Map<string, BusyBand[]>();

    if (!response || !days.length) {
        return result;
    }

    (response.orders || []).forEach((order) => {
        // раздел работы и его шифр — из режима «Разделы»: там строки разложены по разделам
        const sectionOf = new Map<string, string>();
        const codes = new Map<string, string>();

        (order.sections || []).forEach((section) => {
            codes.set(section.id || "", section.code || "");

            (section.rows || []).forEach((row) =>
                (row.segments || []).forEach((segment) => {
                    if (segment.assignmentId && !segment.ghost) {
                        sectionOf.set(segment.assignmentId, section.id || "");
                    }
                })
            );
        });

        (order.rows || []).forEach((row) => {
            if (row.kind !== "employer") {
                return;
            }

            (row.segments || []).forEach((segment) => {
                if (segment.ghost) {
                    return;
                }

                const start = lowerBound(days, segment.from);
                const end = upperBound(days, segment.to);

                if (start > end) {
                    return;
                }

                const contentGuid = segment.assignmentId ? sectionOf.get(segment.assignmentId) || "" : "";
                const list = result.get(row.id) || [];

                list.push({
                    orderId: order.id,
                    contentGuid,
                    start,
                    length: end - start + 1,
                    from: days[start],
                    to: days[end],
                    inventory: order.fullInventoryNumber,
                    title: `${order.fullInventoryNumber} ${order.name}`,
                    sectionCode: contentGuid ? codes.get(contentGuid) || "" : "",
                });

                result.set(row.id, list);
            });
        });
    });

    // соседние отрезки одного проекта и раздела (разные работы подряд) — одной полосой
    result.forEach((list, employerId) => result.set(employerId, merge(list, days)));

    return result;
}

function merge(list: BusyBand[], days: string[]): BusyBand[] {
    const sorted = list
        .slice()
        .sort(
            (left, right) =>
                left.orderId.localeCompare(right.orderId) ||
                left.contentGuid.localeCompare(right.contentGuid) ||
                left.start - right.start
        );

    const result: BusyBand[] = [];

    sorted.forEach((band) => {
        const last = result[result.length - 1];

        if (
            last &&
            last.orderId === band.orderId &&
            last.contentGuid === band.contentGuid &&
            band.start <= last.start + last.length
        ) {
            const end = Math.max(last.start + last.length, band.start + band.length);
            last.length = end - last.start;
            last.to = days[end - 1];
        } else {
            result.push({ ...band });
        }
    });

    return result.sort((left, right) => left.start - right.start);
}

/**
 * Занятость для строки сотрудника в проекте: всё, кроме самой строки. В режиме
 * «Сотрудники» (section не задан) — другие проекты; в режиме «Разделы» — ещё и другие
 * разделы этого проекта.
 */
export function foreignBusy(bands: BusyBand[] | undefined, orderId: string, section?: string): BusyBand[] {
    return (bands || []).filter(
        (band) => band.orderId !== orderId || (section !== undefined && band.contentGuid !== section)
    );
}

function lowerBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] < value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low;
}

function upperBound(days: string[], value: string): number {
    let low = 0;
    let high = days.length;

    while (low < high) {
        const middle = (low + high) >> 1;

        if (days[middle] <= value) {
            low = middle + 1;
        } else {
            high = middle;
        }
    }

    return low - 1;
}
